import styled from 'styled-components';

import type { FontWeight } from '@wecasa/common/src/types/components';

import { useIsDesktop, useIsDesktopOrTablet } from '../../../styles/media-queries';
import BaseText, { type EmphasisStyle, type TextProps } from '../base-text';
import type { TextVariantProps } from './type';

const WECASA_FONT = 'var(--font-wecasa)';
const INSTRUMENT_SERIF_FONT = 'var(--font-instrument-serif)';

type VariantSpec = {
  fontFamily?: string;
  fontSize: number;
  fontWeight: FontWeight;
  lineHeight: number;
  italic?: boolean;
  // Secondary/Primary variants forward their level's Tertiary spec as the emphasis style so that
  // italic (`__…__`) segments render as Tertiary (Instrument Serif) at the Tertiary optical size.
  emphasisStyle?: EmphasisStyle;
};

type TitleComponent = (props: TextVariantProps) => ReturnType<typeof BaseText>;

// A size exposes its 3 variants; bare `Title.<size>` renders Secondary.
type TitleWithVariants = TitleComponent & {
  Primary: TitleComponent;
  Secondary: TitleComponent;
  Tertiary: TitleComponent;
};

/*
 * Line-heights are written as `figmaLineHeightPx / fontSizePx` so the unitless
 * multiplier stays readable and traceable to the Figma title tokens.
 */
const makeVariant =
  (spec: VariantSpec): TitleComponent =>
  ({ children, ...props }: TextVariantProps) => (
    <BaseText {...props} {...spec}>
      {children}
    </BaseText>
  );

// XXXL is the only responsive title: as a landing title it shrinks 58 desktop → 48 mobile.
// Landings use Primary (Wecasa brand), so ONLY Primary is responsive. Secondary/Tertiary keep a
// fixed canonical size (58 / 70), and since a combined title is always Secondary + inline Tertiary
// (never Primary), combined titles never shrink on mobile either.
const makeResponsiveXXXL =
  (spec: Omit<VariantSpec, 'fontSize'>): TitleComponent =>
  ({ children, ...props }: TextVariantProps) => {
    const isDesktop = useIsDesktop();

    return (
      <BaseText {...props} {...spec} fontSize={isDesktop ? 58 : 48}>
        {children}
      </BaseText>
    );
  };

// Bare `Title.<size>` renders Secondary; `Title.<size>.Primary` / `.Tertiary` opt into the brand/editorial fonts.
const withVariants = (variants: {
  primary: TitleComponent;
  secondary: TitleComponent;
  tertiary: TitleComponent;
}): TitleWithVariants =>
  Object.assign(variants.secondary, {
    Primary: variants.primary,
    Secondary: variants.secondary,
    Tertiary: variants.tertiary,
  });

// SM — Work Sans Bold only (no Primary/Tertiary variant).
const SM = makeVariant({ fontSize: 16, fontWeight: 700, lineHeight: 19 / 16 });

/*
 * Each level's Tertiary spec is named so it can be reused twice: as the standalone
 * `.Tertiary` variant, and as the `emphasisStyle` forwarded to Secondary/Primary so
 * italic (`__…__`) segments render as Tertiary at the Tertiary optical size.
 * NB: `italic: true` is honored by the standalone `.Tertiary` variant; when used as
 * `emphasisStyle` it is ignored (the segment's own `__…__` drives the slant) — keep it anyway.
 */
const MD_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 24,
  fontWeight: 400,
  lineHeight: 26 / 24,
};
const MD = withVariants({
  secondary: makeVariant({ fontSize: 20, fontWeight: 900, lineHeight: 23 / 20, emphasisStyle: MD_TERTIARY }),
  primary: makeVariant({
    fontFamily: WECASA_FONT,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 23 / 20,
    emphasisStyle: MD_TERTIARY,
  }),
  tertiary: makeVariant(MD_TERTIARY),
});

const LG_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 31,
  fontWeight: 400,
  lineHeight: 33.5 / 31,
};
const LG = withVariants({
  secondary: makeVariant({ fontSize: 26, fontWeight: 900, lineHeight: 30.5 / 26, emphasisStyle: LG_TERTIARY }),
  primary: makeVariant({
    fontFamily: WECASA_FONT,
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 30.5 / 26,
    emphasisStyle: LG_TERTIARY,
  }),
  tertiary: makeVariant(LG_TERTIARY),
});

const XL_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 38,
  fontWeight: 400,
  lineHeight: 40.5 / 38,
};
const XL = withVariants({
  secondary: makeVariant({ fontSize: 32, fontWeight: 900, lineHeight: 37.5 / 32, emphasisStyle: XL_TERTIARY }),
  primary: makeVariant({
    fontFamily: WECASA_FONT,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 37.5 / 32,
    emphasisStyle: XL_TERTIARY,
  }),
  tertiary: makeVariant(XL_TERTIARY),
});

const XXL_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 46,
  fontWeight: 400,
  lineHeight: 49.6 / 46,
};
const XXL = withVariants({
  secondary: makeVariant({ fontSize: 38, fontWeight: 900, lineHeight: 44.6 / 38, emphasisStyle: XXL_TERTIARY }),
  primary: makeVariant({
    fontFamily: WECASA_FONT,
    fontSize: 38,
    fontWeight: 700,
    lineHeight: 44.6 / 38,
    emphasisStyle: XXL_TERTIARY,
  }),
  tertiary: makeVariant(XXL_TERTIARY),
});

const XXXL_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 70,
  fontWeight: 400,
  lineHeight: 71 / 70,
};
const XXXL = withVariants({
  secondary: makeVariant({ fontSize: 58, fontWeight: 900, lineHeight: 55 / 58, emphasisStyle: XXXL_TERTIARY }),
  primary: makeResponsiveXXXL({
    fontFamily: WECASA_FONT,
    fontWeight: 700,
    lineHeight: 55 / 58,
    emphasisStyle: XXXL_TERTIARY,
  }),
  tertiary: makeVariant(XXXL_TERTIARY),
});

const XXXXL_TERTIARY: VariantSpec = {
  fontFamily: INSTRUMENT_SERIF_FONT,
  italic: true,
  fontSize: 96,
  fontWeight: 400,
  lineHeight: 99 / 96,
};
const XXXXL = withVariants({
  secondary: makeVariant({ fontSize: 80, fontWeight: 900, lineHeight: 76 / 80, emphasisStyle: XXXXL_TERTIARY }),
  primary: makeVariant({
    fontFamily: WECASA_FONT,
    fontSize: 80,
    fontWeight: 700,
    lineHeight: 76 / 80,
    emphasisStyle: XXXXL_TERTIARY,
  }),
  tertiary: makeVariant(XXXXL_TERTIARY),
});

const Title = (props: TextVariantProps) => <MD {...props} />;

Title.SM = SM;
Title.MD = MD;
Title.LG = LG;
Title.XL = XL;
Title.XXL = XXL;
Title.XXXL = XXXL;
Title.XXXXL = XXXXL;

export type TitleSize = 'SM' | 'MD' | 'LG' | 'XL' | 'XXL' | 'XXXL' | 'XXXXL';

const StyledMobileScreenTitle = styled(Title.MD.Primary)`
  margin: 0;
  padding: 0;
`;

const StyledDesktopScreenTitle = styled(Title.LG.Primary)`
  margin: 0;
  padding: 0;
`;
export const ScreenTitle = ({ children, ...props }: TextProps) => {
  const isDesktopOrTablet = useIsDesktopOrTablet();
  const HeaderTitle = isDesktopOrTablet ? StyledDesktopScreenTitle : StyledMobileScreenTitle;

  return (
    <HeaderTitle htmlTag='h1' {...props}>
      {children}
    </HeaderTitle>
  );
};

const SectionTitleText = styled(SM)`
  padding: 10px 20px;
  margin: 0;
`;

export const SectionTitle = ({ children, ...props }: TextProps) => {
  const isDesktopOrTablet = useIsDesktopOrTablet();

  return (
    <SectionTitleText centered={isDesktopOrTablet} htmlTag='h2' {...props}>
      {children}
    </SectionTitleText>
  );
};

export default Title;
